import { Menu, Xmark } from 'iconoir-react'
import { AnimatePresence, m } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { NavLink } from 'react-router-dom'
import { useFocusTrap } from '@/lib/use-focus-trap'
import { cn } from '@/lib/utils'

type NavItem = { to: string; label: string }

// A real slide-in panel instead of just hiding the nav links below 700px --
// the pill nav had no way to reach "What We Do" on a phone before this.
// Locks body scroll while open, closes on Escape or a link tap.
export function MobileMenu({ links }: { links: NavItem[] }) {
  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  useFocusTrap(panelRef, open)

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        aria-expanded={open}
        className="flex h-11 w-11 items-center justify-center rounded-full text-ink min-[700px]:hidden"
      >
        <Menu width={22} height={22} />
      </button>
      {createPortal(
        <AnimatePresence>
          {open && (
          <m.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
            tabIndex={-1}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 flex flex-col bg-canvas outline-none min-[700px]:hidden"
          >
            <div className="flex items-center justify-between px-4 py-4">
              <span className="font-display text-[26px] italic tracking-[-0.01em] text-ink">Flowscale</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
                className="flex h-11 w-11 items-center justify-center rounded-full text-ink"
              >
                <Xmark width={22} height={22} />
              </button>
            </div>

            <nav className="flex flex-1 flex-col gap-1 overflow-y-auto px-4 pt-6">
              {links.map((link, i) => (
                <m.div
                  key={link.to}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.06 + i * 0.05, ease: [0.22, 1, 0.36, 1] }}
                >
                  <NavLink
                    to={link.to}
                    end={link.to === '/'}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        'block border-b border-n-200 py-5 font-display text-[clamp(28px,9vw,40px)] tracking-[-0.01em]',
                        isActive ? 'text-flow italic' : 'text-ink',
                      )
                    }
                  >
                    {link.label}
                  </NavLink>
                </m.div>
              ))}
            </nav>

            <div className="flex flex-col gap-2 px-4 py-8 text-body text-n-500">
              <a href="tel:+19568871317" className="text-ink">
                (956) 887-1317
              </a>
              <a href="mailto:FlowScale1@outlook.com">FlowScale1@outlook.com</a>
            </div>
          </m.div>
          )}
        </AnimatePresence>,
        document.body,
      )}
    </>
  )
}
